<template>
    <div class="flex w-full max-w-[660px] min-h-[440px] flex-col items-center overflow-hidden rounded-[20px] bg-white">
        <!-- 标题区域 -->
        <div class="flex h-[120px] w-full flex-col pl-10 pr-6 pt-8 bg-gradient-to-br from-[#DDF8ED] via-[#DDF0F8] to-[#B9D9FF]">
            <div class="text-sm font-medium text-[#0764E9]">
                {{ t(`${role}.title`) }}
            </div>
            <h2 class="text-2xl font-semibold leading-[1.33] text-[#020A1A]">
                {{ t(`${role}.heading`) }}
            </h2>
        </div>

        <!-- 功能列表 -->
        <div class="flex w-full flex-col gap-3 px-20 pt-6">
            <p class="mb-3 text-sm leading-[1.43] text-[#020A1A]">
                {{ t(`${role}.description`) }}
            </p>
            <div
                v-for="(feature) in features"
                :key="feature"
                class="flex items-start gap-2"
            >
                <PtIcon icon="SolidTickCircle" class="mt-0.5 text-xl flex-shrink-0 fill-[#0764E9]" />
                <span class="text-sm leading-[1.43] text-[#495973]">
                    {{ t(`${role}.features.${feature}`) }}
                </span>
            </div>
        </div>

        <!-- 升级按钮 -->
        <div class="w-full flex justify-center px-6 pt-10 pb-6">
            <div class="relative">
                <!-- 推荐标签 -->
                <div class="absolute -top-[24px] right-0 rounded-t-lg bg-[#FFC700] px-3 py-1 text-xs font-medium text-[#020A1A]">
                    {{ t(`${role}.recommendation`) }}
                </div>
                <button
                    class="flex h-10 w-[304px] items-center justify-center gap-2 rounded bg-[#0764E9] text-base font-semibold leading-[1.5] text-white transition-all hover:bg-[#0456D1]"
                    @click="handleUpgrade"
                >
                    {{ t(`${role}.upgradeButton`) }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { PtIcon } from 'patsnap-biz';
import '@patsnap-ui/icon/assets/solid/TickCircle.svg';
import { useTrack } from '../../composables/use-track';
import { openPricingUpgradeDialog } from '@eureka/shared/plan-dialog';
import { openClueReport } from '@eureka/shared/clue-report';
import { EurekaPackageType } from '@eureka/services/src/apis/common.api.types';
import { cn, en, jp, tw } from './locales';
import { computedAsync } from '@vueuse/core';
import { lsPlanDeferred } from '@eureka/utils/ls-plan';

interface Props {
    role?: 'personal_basic' | 'personal_pro';
    eventDetail?: Record<string, any>;
    onClose?: () => void;
}

const props = withDefaults(defineProps<Props>(), {
    role: 'personal_basic',
});

const track = useTrack();

const isEconomy = computedAsync(async() => {
    const planValue = await lsPlanDeferred.promise;
    return planValue === 'economy';
}, false);

const { t } = useI18n({
    messages: {
        cn,
        en,
        jp,
        tw,
    },
});

// 根据角色动态生成功能列表
const features = computed(() => {
    const isEconomyValue = isEconomy.value;
    return props.role === 'personal_pro' ? [isEconomyValue ? 'economy_0' : '0', '1', '2', '3', '4', '5', '6', '7'] : [isEconomyValue ? 'economy_0' : '0', '1', '2', '3'];
});

// 处理升级操作
const handleUpgrade = () => {
    const fromTrigger = props.eventDetail?.trigger_source || 'paywall'
    const eventDetailStr = JSON.stringify({
        product_line: 'eureka_ls',
        from_trigger: fromTrigger,
        ...(props.eventDetail || {}),
    })

    if (props.role === 'personal_basic') {
        track({
            eventType: 'SS EUREKA UPGRADE PAGE',
            sourceType: 'click_subscribe_pro',
            moduleType: 'LS',
            eventDetail: eventDetailStr,
        })
        // 弹框调用 plan 弹框
        openPricingUpgradeDialog(props.eventDetail ? { eventDetail: props.eventDetail } : undefined);
        props.onClose?.();
    } else {
        // 弹框调用联系销售
        track({
            eventType: 'FF LS PLG',
            moduleType: 'LS',
            sourceType: 'contact-sales-enterprise',
            otherParam: 'enterprise',
            eventDetail: eventDetailStr,
        });

        openClueReport({
            pkgType: EurekaPackageType.LS_COMMERCIAL,
            version: 'LS_PLG',
        });
        props.onClose?.();
    }
};
</script>
